import {useRouter} from 'next/router';
import type {AxiosError, AxiosResponse} from 'axios';
import {MutationOptions, useMutation} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useGetMeQueryData, useSetMeQueryData} from '@eksab/hooks';

import {useInvalidatePurchasedBadges} from '@eksab/features/profile/badges/hooks';

import type {BuyPackageRequest, PaymentMethod, BuyPackageResponse, PaymentMethodDetails} from '../types';

type PartialBuyPackageRequest = Omit<BuyPackageRequest, keyof PaymentMethodDetails> & Partial<PaymentMethodDetails>;

export const useBuyPackageMutation = <T extends PaymentMethod>(
  params: PartialBuyPackageRequest & {payment_method?: T},
  options?: MutationOptions<BuyPackageResponse<T>, AxiosError, (PaymentMethodDetails & {payment_method: T}) | void>,
) => {
  const {push} = useRouter();

  const setMeQueryData = useSetMeQueryData();
  const getMeQueryData = useGetMeQueryData();
  const invalidatePurchasedBadges = useInvalidatePurchasedBadges();

  return useMutation<BuyPackageResponse<T>, AxiosError, (PaymentMethodDetails & {payment_method: T}) | void>(
    (paymentMethodDetails) =>
      api
        .post<
          BuyPackageRequest,
          AxiosResponse<{data: BuyPackageResponse<T>}>
        >('v3/payments/buy-package', {...params, ...paymentMethodDetails})
        .then((res) => res.data.data),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (data.next.action !== 'add-coins') {
          options?.onSuccess?.(data, variables, context);
          return;
        }

        const me = getMeQueryData()!;
        me.coins = data.next.coins;
        setMeQueryData(me);

        if (params.package_type === 'badge') {
          invalidatePurchasedBadges();
        }

        const paymentStateURL = `/payment/${params.package_type === 'coin' ? 'coins' : 'badges'}/${params.package_id}/state?transaction=${data.reference.number}&status=${data.status}`;
        push(paymentStateURL);
      },
    },
  );
};
